import {
  Button, Grid, Table, TableBody, TableCell, TableContainer,
  TableRow, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, TableHead, TableFooter,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus, Trash01 } from '../../assets';
import { useState, useContext, useEffect } from 'react';
import { UserContext } from '../../components/shared/useUser';
import { TaskCategoriesClient, TaskCategoryDto } from '../../app/services/api/tasksClient';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';

const client = TaskCategoriesClient;

const ManageTaskCategories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermissionTo } = useContext(UserContext);
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskCategoriesCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['TaskCategoriesEdit']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['TaskCategoriesDelete']) : false;

  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDeleteId, setOpenDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState({ name: '', description: '', sortOrder: 10 });
  const [errors, setErrors] = useState<{ name?: string; description?: string; sortOrder?: string }>({});

  useEffect(() => {
    client.getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const openCreateDialog = () => {
    if (!canCreate) return;
    setFormValues({ name: '', description: '', sortOrder: 10 });
    setEditId(null);
    setOpenCreate(true);
  };
  const openEditDialog = (row: TaskCategoryDto) => {
    if (!canEdit) return;
    setFormValues({ name: row.name, description: row.description, sortOrder: 10 });
    setEditId(row.id);
    setOpenCreate(true);
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!formValues.name.trim()) e.name = 'Required';
    if (isNaN(formValues.sortOrder)) e.sortOrder = 'Invalid';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const dto: TaskCategoryDto = {
      id: editId ?? 0,
      franchiseId: 1,
      name: formValues.name,
      description: formValues.description,
    };

    if (editId === null) {
      await client.createCategory(dto);
    } else {
      await client.updateCategory(editId, dto);
    }

    const updated = await client.getCategories();
    setCategories(updated);
    handleClose();
  };

  const handleClose = () => { setOpenCreate(false); setOpenDeleteId(null); };

  const handleDelete = async (id: number) => {
    await client.deleteCategory(id);
    const updated = await client.getCategories();
    setCategories(updated);
    setOpenDeleteId(null);
  };

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}><PageTitle>Task Categories</PageTitle></Grid>
        <Grid item
          xs={isMobile ? 8 : 9}></Grid>
        <Grid item
          xs={isMobile ? 4 : 3}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {canCreate && <Button size='lg'
            sx={{ mr: 3 }}
            variant='primary'
            onClick={openCreateDialog}><Plus />Create New</Button>}
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {<Pill text={`${categories.length} Categories`}></Pill>}
            </TableHeader>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map(row => (
                    <TableRow key={row.id}
                      onClick={() => openEditDialog(row)}
                      hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'flex', gap: 1 }}>
                        {canDelete && (
                          <Trash01
                            color={theme.palette.error[500]}
                            className='delete-icon'
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenDeleteId(row.id); }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 && <TableRow><TableCell colSpan={3}>No categories found.</TableCell></TableRow>}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3}></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>
      <Dialog open={openCreate && (canCreate || canEdit)}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>{editId === null ? 'Create Category' : 'Edit Category'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label='Name'
            value={formValues.name}
            onChange={e => setFormValues(v => ({ ...v, name: e.target.value }))}
            error={!!errors.name}
            helperText={errors.name}
            autoFocus
          />
          <TextField
            label='Description'
            multiline
            minRows={3}
            value={formValues.description}
            onChange={e => setFormValues(v => ({ ...v, description: e.target.value }))}
          />
          <TextField
            label='Sort Order'
            type='number'
            value={formValues.sortOrder}
            onChange={e => setFormValues(v => ({ ...v, sortOrder: parseInt(e.target.value, 10) }))}
            error={!!errors.sortOrder}
            helperText={errors.sortOrder}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='secondary'
            onClick={handleClose}>Cancel</Button>
          <Button variant='primary'
            onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteId !== null && canDelete}
        onClose={() => setOpenDeleteId(null)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>Are you sure you want to delete this category?</DialogContent>
        <DialogActions>
          <Button variant='secondary'
            onClick={() => setOpenDeleteId(null)}>Cancel</Button>
          <Button color='warning'
            onClick={() => openDeleteId !== null && handleDelete(openDeleteId)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default ManageTaskCategories;